"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Invoice } from "@/types";
import { PaymentOutput } from "@/types/payment.types";
import { toast } from "sonner";
import {
  generateInvoicePdf,
  generatePaymentReceiptPdf,
} from "@/lib/invoicePdf";
import { formatCurrency } from "@/lib/utils";
import { paymentService, PaymentFilterParams } from "@/services/paymentService";
import { appointmentService } from "@/services/appointmentService";
import CommonHeader from "./common/CommonHeader";
import { BillingSummaryCards } from "./billing/BillingSummaryCards";
import { BillingFilters } from "./billing/BillingFilters";
import { InvoicesTable } from "./billing/InvoicesTable";
import { InvoiceDetailDialog } from "./billing/InvoiceDetailDialog";
import { PaymentDetailDialog } from "./billing/PaymentDetailDialog";

export function BillingManagement() {
  const { invoices, customers, vehicles, appointments } = useApp();

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentOutput | null>(
    null,
  );
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [payments, setPayments] = useState<PaymentOutput[]>([]);
  const [overview, setOverview] = useState<{
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
  }>({
    totalAmount: 0,
    paidAmount: 0,
    pendingAmount: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);

  // Helper function to calculate overview from payments array
  const calculateOverviewFromPayments = (paymentsList: PaymentOutput[]) => {
    const total = paymentsList.reduce(
      (sum, payment) => sum + (payment.totalAmount || 0),
      0,
    );
    const paid = paymentsList
      .filter((payment) => payment.status === "COMPLETED")
      .reduce((sum, payment) => sum + (payment.paidAmount || 0), 0);
    const pending = paymentsList
      .filter((payment) => payment.status === "PENDING")
      .reduce((sum, payment) => sum + (payment.dueAmount || 0), 0);

    return {
      totalAmount: total,
      paidAmount: paid,
      pendingAmount: pending,
    };
  };

  // Build filter params
  const getFilterParams = useCallback((): PaymentFilterParams => {
    const params: PaymentFilterParams = {};
    if (startDate) {
      params.startDate = startDate;
    }
    if (endDate) {
      params.endDate = endDate;
    }
    return params;
  }, [startDate, endDate]);

  // Fetch payments and overview
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = getFilterParams();
      const [paymentsData, overviewData] = await Promise.all([
        paymentService.getAllPayments(params),
        paymentService.getPaymentOverview(params),
      ]);

      setPayments(paymentsData);

      // Use API overview data if available, otherwise calculate from payments
      if (overviewData && overviewData.totalAmount !== undefined) {
        setOverview({
          totalAmount: overviewData.totalAmount || 0,
          paidAmount: overviewData.paidAmount || 0,
          pendingAmount: overviewData.pendingAmount || 0,
        });
      } else {
        // Fallback: calculate from payments array
        console.warn(
          "API overview data structure unexpected, calculating from payments",
        );
        const calculatedOverview = calculateOverviewFromPayments(paymentsData);
        setOverview(calculatedOverview);
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
      toast.error("Failed to load payment data");
      // Try to calculate from payments if we have them
      try {
        const params = getFilterParams();
        const paymentsData = await paymentService.getAllPayments(params);
        setPayments(paymentsData);
        const calculatedOverview = calculateOverviewFromPayments(paymentsData);
        setOverview(calculatedOverview);
      } catch (fallbackError) {
        console.error("Error in fallback calculation:", fallbackError);
      }
    } finally {
      setLoading(false);
    }
  }, [getFilterParams]);

  // Fetch on mount and when date filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for billing-refresh events (triggered when appointment vehicles are updated)
  useEffect(() => {
    const handleBillingRefresh = () => {
      console.log("Billing refresh triggered by appointment update");
      fetchData();
    };
    window.addEventListener("billing-refresh", handleBillingRefresh);
    return () => {
      window.removeEventListener("billing-refresh", handleBillingRefresh);
    };
  }, [fetchData]);

  // Handle clearing date filters
  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
  };

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    let filtered = invoices;

    if (filterStatus !== "all") {
      filtered = filtered.filter((inv) => inv.paymentStatus === filterStatus);
    }

    if (filterPaymentMethod !== "all") {
      filtered = filtered.filter(
        (inv) => inv.paymentMethod === filterPaymentMethod,
      );
    }

    return filtered.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [invoices, filterStatus, filterPaymentMethod]);

  // Filter payments
  const filteredPayments = useMemo(() => {
    let filtered = payments;

    // Map invoice filter status values to payment status values
    if (filterStatus !== "all") {
      const statusMap: Record<string, string> = {
        paid: "COMPLETED",
        pending: "PENDING",
        cancelled: "CANCELLED",
      };
      const paymentStatus = statusMap[filterStatus];
      if (paymentStatus) {
        filtered = filtered.filter((pay) => pay.status === paymentStatus);
      }
    }

    // Map invoice filter payment method values to payment mode values
    if (filterPaymentMethod !== "all") {
      const methodMap: Record<string, string> = {
        cash: "CASH",
        card: "CARD",
        "bank-transfer": "BANK_TRANSFER",
      };
      const paymentMode = methodMap[filterPaymentMethod];
      if (paymentMode) {
        filtered = filtered.filter((pay) => pay.mode === paymentMode);
      }
    }

    // Sort by date (most recent first)
    return filtered.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA;
    });
  }, [payments, filterStatus, filterPaymentMethod]);

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      paid: "bg-green-500 hover:bg-green-600",
      pending: "bg-yellow-500 hover:bg-yellow-600",
      cancelled: "bg-red-500 hover:bg-red-600",
    };

    return (
      <Badge className={colors[status] || ""}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const viewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsDetailDialogOpen(true);
  };

  const downloadInvoice = (invoice: Invoice) => {
    try {
      const customer = customers.find((c) => c.id === invoice.customerId);
      const appointment = invoice.appointmentId
        ? appointments.find((a) => a.id === invoice.appointmentId)
        : undefined;

      // Resolve multiple vehicles if invoice is linked to an appointment
      const invoiceVehicles: Vehicle[] = [];

      // If valid appointment with vehicles, use those
      if (appointment?.vehicleIds?.length > 0) {
        appointment.vehicleIds.forEach((vId) => {
          const v = vehicles.find((veh) => veh.id === vId);
          if (v) invoiceVehicles.push(v);
        });
      }
      // Fallback to single vehicle from invoice
      else if (invoice.vehicleId) {
        const vehicle = vehicles.find((v) => v.id === invoice.vehicleId);
        if (vehicle) invoiceVehicles.push(vehicle);
      }

      if (!customer || invoiceVehicles.length === 0) {
        toast.error("Missing customer or vehicle information");
        return;
      }

      generateInvoicePdf({
        invoice,
        customer,
        vehicles: invoiceVehicles,
        appointment,
      });

      toast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice");
    }
  };

  const handleViewPayment = (payment: PaymentOutput) => {
    setSelectedPayment(payment);
    setIsPaymentDialogOpen(true);
  };

  const handleUpdatePaymentStatus = async (
    payment: PaymentOutput,
    newStatus: string,
  ) => {
    try {
      const updatedPayment = await paymentService.updatePaymentStatus(
        payment.id,
        newStatus,
      );

      // Merge updated payment with existing data to preserve customer/vehicle info
      const mergedPayment: PaymentOutput = {
        ...payment, // Keep existing customer/vehicle data
        ...updatedPayment, // Apply backend updates (status, etc.)
        customer: updatedPayment.customer || payment.customer,
        vehicle: updatedPayment.vehicle || payment.vehicle,
      };

      // Update the payment in the local state
      setPayments((prevPayments) =>
        prevPayments.map((p) => (p.id === payment.id ? mergedPayment : p)),
      );

      // Update selectedPayment if it's the same payment being updated
      if (selectedPayment && selectedPayment.id === payment.id) {
        setSelectedPayment(mergedPayment);
      }

      // Refresh overview to reflect status changes
      try {
        const overviewData = await paymentService.getPaymentOverview();
        setOverview({
          totalAmount: overviewData.totalAmount || 0,
          paidAmount: overviewData.paidAmount || 0,
          pendingAmount: overviewData.pendingAmount || 0,
        });
      } catch (error) {
        console.error("Error refreshing overview:", error);
        // Fallback: recalculate from updated payments state
        setPayments((prevPayments) => {
          const calculatedOverview =
            calculateOverviewFromPayments(prevPayments);
          setOverview(calculatedOverview);
          return prevPayments;
        });
      }

      toast.success(`Payment status updated to ${newStatus} successfully`);

      // If payment was cancelled, also cancel the associated appointment
      if (newStatus === "CANCELLED" && payment.refId) {
        try {
          await appointmentService.updateAppointmentStatus(
            payment.refId,
            "CANCELLED",
          );
          toast.success("Associated appointment has been cancelled");
          // Trigger appointment refresh to reflect status change
          window.dispatchEvent(new CustomEvent("appointments-refresh"));
        } catch (appointmentError) {
          console.error(
            "Error cancelling associated appointment:",
            appointmentError,
          );
          // Don't throw - payment was updated successfully, appointment sync can fail silently
        }
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("Failed to update payment status");
    }
  };

  const handleUpdatePaymentAmount = async (
    payment: PaymentOutput,
    newAmount: number,
  ) => {
    try {
      const updatedPayment = await paymentService.updatePaymentAmount(
        payment.id,
        newAmount,
      );

      // Update the payment in the local state
      setPayments((prevPayments) =>
        prevPayments.map((p) => (p.id === payment.id ? updatedPayment : p)),
      );

      // Update selectedPayment if it's the same payment being updated
      if (selectedPayment && selectedPayment.id === payment.id) {
        setSelectedPayment(updatedPayment);
      }

      // Refresh overview to reflect amount changes
      try {
        const overviewData = await paymentService.getPaymentOverview();
        setOverview({
          totalAmount: overviewData.totalAmount || 0,
          paidAmount: overviewData.paidAmount || 0,
          pendingAmount: overviewData.pendingAmount || 0,
        });
      } catch (error) {
        console.error("Error refreshing overview:", error);
        // Fallback: recalculate from updated payments state
        setPayments((prevPayments) => {
          const calculatedOverview =
            calculateOverviewFromPayments(prevPayments);
          setOverview(calculatedOverview);
          return prevPayments;
        });
      }

      toast.success(
        `Payment amount updated to ${formatCurrency(newAmount)} successfully`,
      );
    } catch (error) {
      console.error("Error updating payment amount:", error);
      toast.error("Failed to update payment amount");
    }
  };

  const handlePartialPayment = async (
    payment: PaymentOutput,
    amount: number,
  ) => {
    try {
      const updatedPayment = await paymentService.addPartialPayment(
        payment.id,
        amount,
      );

      // Update the payment in the local state
      setPayments((prevPayments) =>
        prevPayments.map((p) => (p.id === payment.id ? updatedPayment : p)),
      );

      // Update selectedPayment if it's the same payment being updated
      if (selectedPayment && selectedPayment.id === payment.id) {
        setSelectedPayment(updatedPayment);
      }

      // Refresh overview to reflect payment changes
      try {
        const overviewData = await paymentService.getPaymentOverview();
        setOverview({
          totalAmount: overviewData.totalAmount || 0,
          paidAmount: overviewData.paidAmount || 0,
          pendingAmount: overviewData.pendingAmount || 0,
        });
      } catch (error) {
        console.error("Error refreshing overview:", error);
        // Fallback: recalculate from updated payments state
        setPayments((prevPayments) => {
          const calculatedOverview =
            calculateOverviewFromPayments(prevPayments);
          setOverview(calculatedOverview);
          return prevPayments;
        });
      }
    } catch (error) {
      console.error("Error recording partial payment:", error);
      throw error; // Re-throw to let the dialog handle the error display
    }
  };

  const handleUpdateDiscount = async (
    payment: PaymentOutput,
    newDiscount: number,
  ) => {
    try {
      const updatedPayment = await paymentService.updateDiscount(
        payment.id,
        newDiscount,
      );

      // Update the payment in the local state
      setPayments((prevPayments) =>
        prevPayments.map((p) => (p.id === payment.id ? updatedPayment : p)),
      );

      // Update selectedPayment if it's the same payment being updated
      if (selectedPayment && selectedPayment.id === payment.id) {
        setSelectedPayment(updatedPayment);
      }

      // Refresh overview to reflect discount changes
      try {
        const overviewData = await paymentService.getPaymentOverview();
        setOverview({
          totalAmount: overviewData.totalAmount || 0,
          paidAmount: overviewData.paidAmount || 0,
          pendingAmount: overviewData.pendingAmount || 0,
        });
      } catch (error) {
        console.error("Error refreshing overview:", error);
        // Fallback: recalculate from updated payments state
        setPayments((prevPayments) => {
          const calculatedOverview =
            calculateOverviewFromPayments(prevPayments);
          setOverview(calculatedOverview);
          return prevPayments;
        });
      }

      toast.success(
        `Discount updated to ${formatCurrency(newDiscount)} successfully`,
      );
    } catch (error) {
      console.error("Error updating discount:", error);
      toast.error("Failed to update discount");
    }
  };

  const handleDownloadReceipt = (payment: PaymentOutput) => {
    try {
      // Find customer from customers array if payment.customer has an id
      const customer = payment.customer?.id
        ? customers.find((c) => c.id === payment.customer?.id) || null
        : null;

      // Resolve multiple vehicles if payment is linked to an appointment
      const paymentVehicles: Vehicle[] = [];

      // Find linked appointment
      const linkedAppointment =
        payment.refType === "APPOINTMENT" &&
        payment.refId &&
        appointments.length > 0
          ? appointments.find((a) => a.id === payment.refId)
          : null;

      // If valid appointment with vehicles, use those
      if (linkedAppointment?.vehicleIds?.length > 0) {
        linkedAppointment.vehicleIds.forEach((vId) => {
          const v = vehicles.find((veh) => veh.id === vId);
          if (v) paymentVehicles.push(v);
        });
      }
      // Fallback to single vehicle from payment object
      else if (payment.vehicle?.id) {
        const vehicle = vehicles.find((v) => v.id === payment.vehicle?.id);
        if (vehicle) paymentVehicles.push(vehicle);
      }

      generatePaymentReceiptPdf({
        payment,
        customer: customer || null,
        vehicles: paymentVehicles,
      });

      toast.success("Payment receipt downloaded successfully");
    } catch (error) {
      console.error("Error downloading payment receipt:", error);
      toast.error("Failed to download payment receipt");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <CommonHeader
        title="Billing & Invoices"
        description="Manage invoices and payments"
      />

      {/* Summary Cards */}
      <BillingSummaryCards totals={overview} />

      {/* Filters */}
      <BillingFilters
        filterStatus={filterStatus}
        filterPaymentMethod={filterPaymentMethod}
        startDate={startDate}
        endDate={endDate}
        onStatusChange={setFilterStatus}
        onPaymentMethodChange={setFilterPaymentMethod}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onClearDates={handleClearDates}
      />

      {/* Invoices & Payments Table */}
      {loading ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              Loading data...
            </div>
          </CardContent>
        </Card>
      ) : (
        <InvoicesTable
          invoices={filteredInvoices}
          payments={filteredPayments}
          customers={customers}
          vehicles={vehicles}
          appointments={appointments}
          getStatusBadge={getStatusBadge}
          onView={viewInvoice}
          onDownload={downloadInvoice}
          onViewPayment={handleViewPayment}
          onUpdatePaymentStatus={handleUpdatePaymentStatus}
          onDownloadReceipt={handleDownloadReceipt}
        />
      )}

      {/* Invoice Detail Dialog */}
      <InvoiceDetailDialog
        open={isDetailDialogOpen}
        onOpenChange={setIsDetailDialogOpen}
        invoice={selectedInvoice}
        customers={customers}
        vehicles={vehicles}
        appointments={appointments}
        getStatusBadge={getStatusBadge}
        onDownload={downloadInvoice}
      />

      {/* Payment Detail Dialog */}
      <PaymentDetailDialog
        open={isPaymentDialogOpen}
        onOpenChange={setIsPaymentDialogOpen}
        payment={selectedPayment}
        customers={customers}
        vehicles={vehicles}
        appointments={appointments}
        onUpdatePaymentStatus={handleUpdatePaymentStatus}
        onUpdatePaymentAmount={handleUpdatePaymentAmount}
        onUpdateDiscount={handleUpdateDiscount}
        onPartialPayment={handlePartialPayment}
        onDownloadReceipt={handleDownloadReceipt}
      />
    </div>
  );
}
